import React from 'react'
import {useHistory} from 'react-router-dom'

import {
  useConsultationsProgramDetailQuery,
  useConsultationsMyAppointmentsQuery,
  type ConsultationsProgramDetailQuery,
  type ConsultationsMyAppointmentsQuery,
} from '@progyny/generated/graphql'
import type {SessionForBooking} from '@progyny/pages/Consultations/components/SingleSession/SingleSessionBooking'
import SingleSessionTabs from '@progyny/pages/Consultations/components/SingleSession/SingleSessionTabs'
import {
  filterUpcomingAppointments,
  filterPastAppointments,
} from '@progyny/utils/appointmentHelpers'

interface SingleSessionOverviewProps {
  programSlug: string
  sessionNumber?: number
  colors: any
  basePath?: string
  showTabs?: boolean
  programData?: ConsultationsProgramDetailQuery
  appointmentsData?: ConsultationsMyAppointmentsQuery
  onDataRefetch?: () => void
}

const SingleSessionOverview: React.FC<SingleSessionOverviewProps> = ({
  programSlug,
  sessionNumber = 1,
  colors,
  basePath,
  showTabs = true,
  programData: programDataProp,
  appointmentsData: appointmentsDataProp,
  onDataRefetch,
}) => {
  const history = useHistory()

  const {data: fetchedProgramData, refetch: refetchProgram} =
    useConsultationsProgramDetailQuery({
      variables: {slug: programSlug},
      skip: !!programDataProp,
    })

  const {data: fetchedAppointmentsData, refetch: refetchAppointments} =
    useConsultationsMyAppointmentsQuery({
      fetchPolicy: 'cache-first',
      skip: !!appointmentsDataProp,
    })

  const programData = programDataProp ?? fetchedProgramData
  const appointmentsData = appointmentsDataProp ?? fetchedAppointmentsData

  const program = programData?.consultations?.program
  const sessionRaw = program?.sessions?.find(
    (s) => s?.sessionNumber === sessionNumber
  )
  const session: SessionForBooking | null | undefined = sessionRaw
    ? {
        id: sessionRaw.id!,
        calcomEventType: sessionRaw.calcomEventType,
        title: sessionRaw.title,
        subtitle: sessionRaw.subtitle,
        description: sessionRaw.description,
        bookingDescription: sessionRaw.bookingDescription,
      }
    : null

  const programAppointments =
    appointmentsData?.consultations?.myAppointments?.filter(
      (appointment) =>
        appointment?.programEnrollment?.program?.slug === programSlug
    ) || []

  const upcomingAppointments = filterUpcomingAppointments(programAppointments)
  const pastAppointments = filterPastAppointments(programAppointments)

  const handleBookingSuccessful = () => {
    history.push(`/consultations/program/${programSlug}`)
  }

  const handleCancelBack = React.useCallback(() => {
    if (onDataRefetch) {
      onDataRefetch()
      return
    }
    refetchProgram()
    refetchAppointments()
  }, [onDataRefetch, refetchProgram, refetchAppointments])

  return (
    <SingleSessionTabs
      programSlug={programSlug}
      sessionNumber={sessionNumber}
      session={session}
      upcomingAppointments={upcomingAppointments}
      pastAppointments={pastAppointments}
      colors={colors}
      basePath={basePath}
      showTabs={showTabs}
      onBookingSuccessful={handleBookingSuccessful}
      onCancelBack={handleCancelBack}
    />
  )
}

export default SingleSessionOverview
